import React, { useState } from "react";
import { CreditCard, Target, Check, Plus, FileText, Users, ChevronDown, Sparkles } from "lucide-react";
import { fmt, fmtDate } from "@/lib/constants";
import { api } from "@/lib/api";
import { toast } from "sonner";
import HelpTip from "@/components/HelpTip";

export const utilTone = (u) => (u == null ? "" : u < 30 ? "text-emerald-500" : u < 50 ? "text-amber-500" : "text-rose-500");
export const utilBar = (u) => (u == null ? "bg-accent" : u < 30 ? "bg-emerald-500" : u < 50 ? "bg-amber-500" : "bg-rose-500");
const utilLabel = (u) => (u == null ? "—" : u < 10 ? "Excellent" : u < 30 ? "Healthy" : u < 50 ? "Elevated" : u < 75 ? "High" : "Maxed out");

export default function CreditUtilization({ credit, plan, onChanged, compact, detailsOnly }) {
  const [busy, setBusy] = useState(null);
  const [showCards, setShowCards] = useState(false);
  if (!credit || credit.cards.length === 0) return null;
  const u = credit.total_utilization;
  const goal = credit.goal_pct;
  const targets = plan?.utilization_targets || [];
  const over = credit.cards.filter(c => c.utilization_target?.pay_to_reach > 0);
  const allIn = over.length > 0 && over.every(c => targets.includes(c.id));

  // Idle ($0-balance) cards: their credit limit still counts toward available credit, lowering overall utilization.
  const idleCards = credit.cards.filter(c => c.balance <= 0.005 && (c.credit_limit || 0) > 0);
  const idleLimit = idleCards.reduce((s, c) => s + (c.credit_limit || 0), 0);
  const limitWithoutIdle = (credit.total_limit || 0) - idleLimit;
  const utilWithoutIdle = limitWithoutIdle > 0 ? (credit.total_balance / limitWithoutIdle) * 100 : 100;
  const idleDelta = Math.round(utilWithoutIdle) - Math.round(u ?? 0);
  const showIdleNudge = idleCards.length > 0 && u != null && credit.total_balance > 0.005 && idleDelta >= 1;
  const idleNudge = showIdleNudge ? (
    <div className="mt-3 flex items-start gap-2 p-3 rounded-xl border border-emerald-500/25 bg-emerald-500/5" data-testid="credit-idle-nudge">
      <Sparkles className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
      <div className="text-[11px] leading-relaxed">
        Your {idleCards.length === 1 ? "$0-balance card" : `${idleCards.length} $0-balance cards`}{" "}
        (<span className="font-semibold">{idleCards.map(c => c.name).join(", ")}</span>) {idleCards.length === 1 ? "adds" : "add"}{" "}
        <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">{fmt(idleLimit)}</span> of unused credit —
        keeping them open holds your utilization at <span className="font-semibold">{Math.round(u)}%</span> instead of{" "}
        <span className="font-semibold text-rose-500">~{Math.round(utilWithoutIdle)}%</span>. Closing them would hurt your score.
      </div>
    </div>
  ) : null;

  const save = async (next, msg) => {
    setBusy("all");
    try { await api.put("/payoff-plan", { utilization_targets: next }); toast.success(msg); onChanged?.(); }
    catch { toast.error("Could not update plan"); }
    finally { setBusy(null); }
  };
  const toggle = (c) => {
    const on = targets.includes(c.id);
    const next = on ? targets.filter(id => id !== c.id) : [...targets, c.id];
    setBusy(c.id);
    save(next, on ? `${c.name} boost removed from this month's plan` : `Added ${fmt(c.utilization_target.pay_to_reach)} for ${c.name} to this month's plan`);
  };
  const toggleAll = () => save(allIn ? targets.filter(id => !over.some(c => c.id === id)) : Array.from(new Set([...targets, ...over.map(c => c.id)])),
    allIn ? "Utilization boosts removed" : `All cards set to reach ${goal}% this month`);

  const summaryBlock = credit.total_pay_to_goal > 0 ? (
        <div className="mt-3 p-3 rounded-xl border border-indigo-500/30 bg-indigo-500/10" data-testid="credit-goal-summary">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs">
              <div className="font-semibold flex items-center gap-1.5"><Target className="h-3.5 w-3.5 text-indigo-400" /> Pay <span className="font-mono" data-testid="credit-total-pay-to-goal">{fmt(credit.total_pay_to_goal)}</span> to get every card under {goal}%</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {credit.total_boost_in_plan > 0 ? `${fmt(credit.total_boost_in_plan)} boost currently in this month's plan` : "Nothing added to this month's plan yet"}
              </div>
            </div>
            <button onClick={toggleAll} disabled={busy !== null} data-testid="credit-add-all-btn"
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50 ${allIn ? "bg-emerald-500/15 text-emerald-500 border border-emerald-500/30" : "bg-indigo-500 text-white hover:bg-indigo-600"}`}>
              {allIn ? <><Check className="h-3.5 w-3.5" /> All in plan</> : <><Plus className="h-3.5 w-3.5" /> Add all to plan</>}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 text-[11px] text-emerald-500 font-semibold flex items-center gap-1.5" data-testid="credit-goal-met"><Check className="h-3.5 w-3.5" /> Every card is at or under {goal}%</div>
      );

  const memberBlock = credit.by_member?.length > 1 && (
        <div className="mt-4 p-3 rounded-xl border border-border/60 bg-accent/30" data-testid="credit-by-member">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1.5"><Users className="h-3 w-3" /> By person</div>
          <ul className="mt-2 space-y-2.5">
            {credit.by_member.map(m => (
              <li key={m.user_id} data-testid={`credit-member-${m.user_id}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2 font-semibold">
                    {m.picture ? <img src={m.picture} alt="" className="h-5 w-5 rounded-full object-cover" /> : <span className="h-5 w-5 rounded-full bg-accent text-[10px] flex items-center justify-center font-bold">{m.name?.[0]}</span>}
                    {m.name}<span className="text-muted-foreground font-normal">· {m.cards.length} card{m.cards.length === 1 ? "" : "s"}</span>
                  </span>
                  <span className={`font-mono font-bold ${utilTone(m.total_utilization)}`} data-testid={`credit-member-pct-${m.user_id}`}>{m.total_utilization}%</span>
                </div>
                <div className={`mt-1 h-1.5 rounded-full overflow-hidden ${m.total_utilization <= 0 ? "bg-emerald-500" : "bg-accent"}`}>
                  <div className={`h-full rounded-full ${utilBar(m.total_utilization)}`} style={{ width: `${Math.min(m.total_utilization, 100)}%` }} />
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{fmt(m.total_balance)} of {fmt(m.total_limit)}</div>
              </li>
            ))}
          </ul>
        </div>
      );

  const toggleButton = (
      <button onClick={() => setShowCards(s => !s)} data-testid="credit-toggle-details"
        className="mt-4 w-full flex items-center justify-between px-3 py-2 rounded-xl border border-border bg-accent/30 hover:bg-accent text-xs font-semibold transition">
        <span className="flex items-center gap-1.5 text-muted-foreground uppercase tracking-wider text-[10px]"><CreditCard className="h-3 w-3" /> {showCards ? "Hide" : "Show"} {compact ? "details" : "card details"} · {credit.cards.length} card{credit.cards.length === 1 ? "" : "s"}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showCards ? "rotate-180" : ""}`} />
      </button>
    );

  const cardList = (
      <ul className="mt-3 space-y-3" data-testid="credit-card-list">
        {credit.cards.map(c => {
          const t = c.utilization_target;
          const inPlan = targets.includes(c.id);
          const holder = credit.by_member?.length > 1 ? credit.by_member.find(m => m.user_id === c.owner_id || m.cards.some(x => x.id === c.id)) : null;
          return (
            <li key={c.id} data-testid={`credit-card-${c.id}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold truncate">{c.name}{holder && <span className="ml-1.5 text-[10px] font-normal text-muted-foreground" data-testid={`credit-card-holder-${c.id}`}>· {holder.name.split(" ")[0]}</span>}</span>
                <span className={`font-mono font-semibold ${utilTone(c.utilization)}`}>{c.utilization}%</span>
              </div>
              <div className={`mt-1 h-1.5 rounded-full overflow-hidden ${c.utilization <= 0 ? "bg-emerald-500" : "bg-accent"}`}>
                <div className={`h-full rounded-full ${utilBar(c.utilization)}`} style={{ width: `${Math.min(c.utilization, 100)}%` }} />
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{fmt(c.balance)} of {fmt(c.credit_limit)} · {fmt(c.available_credit)} available</div>
              {c.next_statement_date && c.balance > 0 && (
                <div className={`text-[10px] mt-0.5 flex items-center gap-1 ${c.days_to_statement <= 3 ? "text-amber-500 font-semibold" : "text-muted-foreground"}`} data-testid={`credit-statement-${c.id}`}>
                  <FileText className="h-3 w-3" /> Reports to bureaus {fmtDate(c.next_statement_date)} ({c.days_to_statement === 0 ? "today" : `in ${c.days_to_statement}d`}) — pay before then to lower what's reported
                </div>
              )}
              {t?.pay_to_reach > 0 && (
                <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap" data-testid={`credit-target-${c.id}`}>
                  <div className="text-[11px]">
                    Pay <span className="font-mono font-semibold" data-testid={`credit-pay-to-goal-${c.id}`}>{fmt(t.pay_to_reach)}</span> to reach {goal}%
                    {t.boost_over_plan > 0 && <span className="text-muted-foreground"> · +{fmt(t.boost_over_plan)} over plan</span>}
                  </div>
                  <button onClick={() => toggle(c)} disabled={busy !== null} data-testid={`credit-toggle-${c.id}`}
                    className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border transition disabled:opacity-50 ${
                      inPlan ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/30 hover:bg-rose-500/10 hover:text-rose-500 hover:border-rose-500/30" : "border-indigo-500/40 text-indigo-400 hover:bg-indigo-500/10"}`}>
                    {inPlan ? <><Check className="h-3 w-3" /> In plan</> : <><Plus className="h-3 w-3" /> Add to plan</>}
                  </button>
                </div>
              )}
              {t && t.pay_to_reach <= 0 && inPlan && <div className="mt-1 text-[10px] text-emerald-500">Goal reached — boost no longer needed</div>}
            </li>
          );
        })}
      </ul>
    );

  if (detailsOnly) {
    return (
      <div className="p-5 rounded-2xl border border-border bg-card" data-testid="credit-utilization-details">
        {idleNudge}
        {summaryBlock}
        {memberBlock}
        {cardList}
      </div>
    );
  }

  return (
    <div className="p-5 rounded-2xl border border-border bg-card" data-testid="credit-utilization">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1.5"><CreditCard className="h-3 w-3" /> Credit utilization <HelpTip k="utilization" /> <HelpTip k="utilization_goal" /></div>
          <div className={`font-display font-mono font-extrabold text-3xl mt-1 ${utilTone(u)}`} data-testid="credit-total-utilization">{u}%</div>
          <div className="text-xs text-muted-foreground">{fmt(credit.total_balance)} of {fmt(credit.total_limit)} · {fmt(credit.total_available)} available</div>
        </div>
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${utilTone(u)} border-current/30 bg-current/10`} data-testid="credit-total-label">{utilLabel(u)}</span>
      </div>
      <div className={`mt-3 h-2.5 rounded-full overflow-hidden relative ${u <= 0 ? "bg-emerald-500" : "bg-accent"}`}>
        <div className={`h-full rounded-full ${utilBar(u)} transition-[width] duration-700`} style={{ width: `${Math.min(u, 100)}%` }} />
        <div className="absolute top-0 bottom-0 w-px bg-foreground/40" style={{ left: `${goal}%` }} title={`${goal}% guideline`} />
      </div>

      {idleNudge}

      {compact ? (
        <>
          {credit.total_pay_to_goal > 0 && !showCards && (
            <div className="mt-2 text-[11px] text-muted-foreground" data-testid="credit-compact-hint">
              Pay <span className="font-mono font-semibold text-foreground">{fmt(credit.total_pay_to_goal)}</span> to get every card under {goal}%
            </div>
          )}
          {toggleButton}
          {showCards && (
            <div className="space-y-1">
              {summaryBlock}
              {memberBlock}
              {cardList}
            </div>
          )}
        </>
      ) : (
        <>
          {summaryBlock}
          {memberBlock}
          {toggleButton}
          {showCards && cardList}
        </>
      )}
    </div>
  );
}
